import React, { Component } from 'react';
import { connect } from 'react-redux';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { Layout, message, Spin } from 'antd';
import { withTranslation } from 'react-i18next';
import AppRouter from './AppRouter';
import ConnectionStatusTracker from './ConnectionStatusTracker.jsx';
import Header from './components/Header/Header';
import { getUserIdFromToken, isAuthorized, resetRejectedTokenAttempts } from './utils/auth';
import styles from './App.module.less';
import { LoadingOutlined } from '@ant-design/icons';
import NotificationBar from './components/NotificationBar/NotificationBar';
import { genericNetworkErrorSample } from './utils/analytika/events';
import SideBar from './components/SideMenu/SideBar';
import AddLocationModal from './components/AddLocationModal/AddLocationModal.jsx';
import AddAmountModal from './components/AddAmountModal/AddAmountModal';
import { User } from './api/models/user';
import { CreateDeliveryFormState } from './types/store/deliveries';
import { AnalytikaEventsAttrType } from './api/dtos/analytika';
import { ModalsDispatch, ModalsStore } from './redux/models/modals';
import { AppDispatch, AppState } from './types/store';
import { UserGalileoVars } from './types/store/user';
import { AnalytikaV2PageReporter } from './utils/analytika/reporterAnalytikaV2';
import { routes } from './utils/routerHelper';
import SegmentationPage from './screens/onboarding/SegmentationPage';
import ProtectedRoute from './ProtectedRoute';
import EmailVerificationModal from './components/EmailVerificationModal';

const { Content } = Layout;

message.config({
  duration: 3
});

type AppProps = {
  userData: User;
  fetchPaymentMethods: () => Promise<void>;
  fetchCities: () => Promise<void>;
  logoutUser: () => Promise<void>;
  fetchUserWalletConfig: () => Promise<void>;
  isUserAuthorized: boolean;
  setLoadCompleteForBlockingRequests: () => void;
  setDeliveryFormState: (state: CreateDeliveryFormState) => void;
  fetchMerchantConfig: () => Promise<void>;
  publishAnalytikaEvent: (eventAttrs: AnalytikaEventsAttrType) => void;
  fetchMerchantConfigComplete: boolean;
  fetchUserGalileoVarsComplete: boolean;
  userGalileoVars: UserGalileoVars;
  fetchUserGalileoVars: (userId: string) => Promise<void>;
  fetchPickupLocations: () => Promise<void>;
  modals: ModalsStore;
  modalsDispatch: ModalsDispatch;
};

type AppLocalState = {
  isLoading: boolean;
};

export class App extends Component<AppProps, AppLocalState> {
  constructor(props: AppProps) {
    super(props);

    this.state = {
      isLoading: isAuthorized()
    };
  }

  componentDidMount() {
    // TODO: remove after 100% rollout
    const { fetchUserGalileoVars } = this.props;
    fetchUserGalileoVars(getUserIdFromToken());
    if (isAuthorized()) {
      resetRejectedTokenAttempts();
      this.fetchBlockingRequests();
      this.fetchNonBlockingRequests();
    }
  }

  finalizeBlockingRequests() {
    const { setDeliveryFormState, setLoadCompleteForBlockingRequests } = this.props;
    setDeliveryFormState({ loading: false });
    setLoadCompleteForBlockingRequests();
  }

  async fetchBlockingRequests() {
    const {
      publishAnalytikaEvent,
      fetchUserWalletConfig,
      fetchPaymentMethods,
      fetchMerchantConfig,
      fetchUserGalileoVars
    } = this.props;

    const userId = getUserIdFromToken();

    try {
      await Promise.all([fetchMerchantConfig(), fetchUserGalileoVars(userId)]);
      await Promise.all([fetchUserWalletConfig(), fetchPaymentMethods()]);
      this.finalizeBlockingRequests();
    } catch (error) {
      const errorInfo = error?.response || error;
      publishAnalytikaEvent(genericNetworkErrorSample(errorInfo));
      this.finalizeBlockingRequests();
    }
  }

  fetchNonBlockingRequests() {
    this.props.fetchCities().catch(error => {
      // eslint-disable-next-line no-console
      console.error('Error loading cities ', error);
    });
    this.props.fetchPickupLocations().catch(error => {
      // eslint-disable-next-line no-console
      console.error('Error loading pickup locations', error);
    });
  }

  renderLoadingIcon() {
    const loadingIcon = <LoadingOutlined className={styles.spinnerIcon} spin />;
    return <Spin className={styles.spinner} indicator={loadingIcon} data-testid="AppSpinner" />;
  }

  render() {
    const isUserAuthorized = isAuthorized();
    const { modals, modalsDispatch } = this.props;

    return (
      <Spin size={'small'} spinning={!isUserAuthorized}>
        <BrowserRouter>
          <AnalytikaV2PageReporter />
          <ConnectionStatusTracker />
          <Switch>
            <ProtectedRoute
              exact
              path={routes.segmentationStart}
              component={SegmentationPage}
              componentProps={{ currentStep: 0 }}
            />
            <ProtectedRoute
              exact
              path={routes.existingMerchantDetails}
              component={SegmentationPage}
              componentProps={{ currentStep: 1 }}
            />
            <Route>
              <Layout hasSider className={styles.mainLayout}>
                <SideBar />
                <Content>
                  <Layout className={styles.layout}>
                    {isUserAuthorized && (
                      <>
                        <Header />
                        <NotificationBar />{' '}
                      </>
                    )}
                    <AppRouter />
                    <AddLocationModal
                      visible={modals.isAddNewPickupVisible}
                      onClose={modalsDispatch.toggleIsAddNewPickupVisible}
                    />
                    <AddAmountModal
                      onClose={modalsDispatch.toggleIsAddAmountVisible}
                      visible={modals.isAddAmountVisible}
                    />
                    <EmailVerificationModal
                      onClose={modalsDispatch.toggleIsEmailVerificationVisible}
                      visible={modals.isEmailVerificationVisible}
                    />
                  </Layout>
                </Content>
              </Layout>
            </Route>
          </Switch>
        </BrowserRouter>
      </Spin>
    );
  }
}

const mapState = ({
  user: { userData, isUserAuthorized, userGalileoVars },
  loading: {
    effects: {
      user: { fetchMerchantConfig, fetchUserGalileoVars }
    }
  },
  modals
}: AppState) => ({
  userData,
  isUserAuthorized,
  fetchMerchantConfigComplete: !fetchMerchantConfig,
  fetchUserGalileoVarsComplete: !fetchUserGalileoVars,
  userGalileoVars,
  modals
});

const mapDispatch = ({
  user: {
    fetchPickupLocations,
    fetchPaymentMethods,
    fetchCities,
    logoutUser,
    fetchUserWalletConfig,
    setLoadCompleteForBlockingRequests,
    fetchMerchantConfig,
    publishAnalytikaEvent,
    fetchUserGalileoVars
  },
  deliveries: { setDeliveryFormState },
  modals: modalsDispatch
}: AppDispatch) => ({
  fetchPickupLocations,
  fetchPaymentMethods,
  fetchCities,
  logoutUser,
  fetchUserWalletConfig,
  setLoadCompleteForBlockingRequests,
  setDeliveryFormState,
  fetchMerchantConfig,
  publishAnalytikaEvent,
  fetchUserGalileoVars,
  modalsDispatch
});
// @ts-ignore
export default connect(mapState, mapDispatch)(withTranslation()(App));
